import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

export default function AdminStageTwoDashboard() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [savingThresholds, setSavingThresholds] = useState(false);
  const [bankStatus, setBankStatus] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [decision, setDecision] = useState("approve_progression");
  const [rationale, setRationale] = useState("");
  const [notes, setNotes] = useState("");
  const [resetReason, setResetReason] = useState("verified platform failure");
  const [resetCategory, setResetCategory] = useState("platform_failure");
  const [resetNote, setResetNote] = useState("");

  const load = async () => {
    const [r1, r2, r3] = await Promise.all([
      api.get("/admin/talent-pool/stage-2/results", { params: { limit: 400 } }),
      api.get("/admin/talent-pool/stage-2/thresholds"),
      api.get("/admin/talent-pool/stage-2/bank/status"),
    ]);
    setRows(r1.data || []);
    setThresholds(r2.data || null);
    setBankStatus(r3.data || null);
  };

  useEffect(() => {
    load().catch((err) => {
      toast.error(err.response?.data?.detail || "Could not load Stage 2 admin dashboard");
    });
  }, []);

  const openDetail = async (attemptId) => {
    setSelected(attemptId);
    try {
      const r = await api.get(`/admin/talent-pool/stage-2/results/${attemptId}`);
      setDetail(r.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load attempt detail");
    }
  };

  const saveThresholds = async () => {
    if (!thresholds) return;
    setSavingThresholds(true);
    try {
      await api.put("/admin/talent-pool/stage-2/thresholds", thresholds);
      toast.success("Thresholds updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save thresholds");
    } finally {
      setSavingThresholds(false);
    }
  };

  const submitDecision = async () => {
    if (!selected) return;
    if (rationale.trim().length < 10) {
      toast.error("Decision rationale is required");
      return;
    }
    try {
      await api.post(`/admin/talent-pool/stage-2/results/${selected}/decision`, {
        decision,
        rationale,
        notes: notes || null,
      });
      toast.success("Decision recorded");
      await openDetail(selected);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit decision");
    }
  };

  const submitReset = async () => {
    if (!selected) return;
    if (resetNote.trim().length < 5) {
      toast.error("Administrator note is required for reset");
      return;
    }
    try {
      await api.post(`/admin/talent-pool/stage-2/results/${selected}/reset`, {
        reason: resetReason,
        administrator_note: resetNote,
        incident_category: resetCategory,
        supporting_information: null,
      });
      toast.success("Reset authorised and audited");
      await openDetail(selected);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not authorise reset");
    }
  };

  const publishBank = async (force) => {
    const note = force ? "Force-published for testing" : (window.prompt("Review note") || "");
    if (!note && !force) return;
    setPublishing(true);
    try {
      const r = await api.post("/admin/talent-pool/stage-2/bank/publish", null, {
        params: { review_confirmation: true, review_note: note || "Force-published for testing", force },
      });
      toast.success(`Bank published — ${r.data.published_question_count} questions active`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const submitCandidateReset = async (candidateId, candidateName) => {
    if (!candidateId) return;
    if (resetNote.trim().length < 5) {
      toast.error("Administrator note is required for reset");
      return;
    }
    const ok = window.confirm(`Authorise Stage 2 reset for ${candidateName || "this candidate"}?`);
    if (!ok) return;
    try {
      await api.post(`/admin/talent-pool/stage-2/candidates/${candidateId}/reset-latest`, {
        reason: resetReason,
        administrator_note: resetNote,
        incident_category: resetCategory,
        supporting_information: null,
      });
      toast.success("Candidate reset authorised and audited");
      await load();
      if (selected) {
        await openDetail(selected);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset candidate attempt");
    }
  };

  return (
    <div className="min-h-screen bg-sand p-6 md:p-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-ink font-semibold">Admin</div>
            <h1 className="font-display font-extrabold text-ink text-3xl mt-1">Stage 2 Operational Behaviour Dashboard</h1>
          </div>
          <Link to="/admin/dashboard" className="text-sm text-ink underline-offset-4 hover:underline">Back to admin dashboard</Link>
        </div>

        <section className="mt-6 rounded-lg border border-border bg-card p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-semibold text-ink">Question bank</h2>
              {bankStatus && (
                <div className="mt-1 text-sm text-muted-foreground">
                  Status: <span className={`font-semibold ${bankStatus.status === "published" ? "text-lime-700" : "text-amber-700"}`}>{bankStatus.status}</span>
                  {" · "}{bankStatus.published_questions} / {bankStatus.total_questions} published
                  {bankStatus.updated_at && ` · last updated ${bankStatus.updated_at.slice(0, 10)}`}
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                className="bg-lime-600 text-white hover:bg-lime-700"
                onClick={() => publishBank(true)}
                disabled={publishing}
                data-testid="admin-stage2-force-publish"
              >
                {publishing ? "Publishing…" : "Publish bank for testing"}
              </Button>
              <Button
                variant="outline"
                onClick={() => publishBank(false)}
                disabled={publishing}
                data-testid="admin-stage2-publish"
              >
                Publish reviewed questions
              </Button>
            </div>
          </div>
        </section>

        <section className="mt-6 rounded-lg border border-border bg-card p-5">
          <h2 className="font-semibold text-ink">Threshold configuration</h2>
          {thresholds && (
            <div className="mt-4 grid md:grid-cols-4 gap-3 text-sm">
              {[
                ["pass_overall_pct", "Pass overall %"],
                ["pass_domain_min_pct", "Pass domain minimum %"],
                ["borderline_low_pct", "Borderline low %"],
                ["borderline_high_pct", "Borderline high %"],
                ["borderline_domain_min_pct", "Borderline domain minimum %"],
                ["critical_unsafe_max_pass", "Critical max for pass"],
                ["critical_unsafe_max_borderline", "Critical max for borderline"],
                ["rapid_response_seconds", "Rapid response threshold (s)"],
                ["rapid_response_count_flag", "Rapid response count flag"],
                ["unanswered_count_flag", "Unanswered count flag"],
                ["interruption_count_flag", "Interruption count flag"],
              ].map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input
                    className="mt-1"
                    value={thresholds[key]}
                    onChange={(e) => setThresholds((prev) => ({ ...prev, [key]: Number(e.target.value) }))}
                  />
                </div>
              ))}
            </div>
          )}
          <Button className="mt-4 bg-navy text-bone hover:bg-lime-600 hover:text-ink" onClick={saveThresholds} disabled={savingThresholds}>
            {savingThresholds ? "Saving..." : "Save thresholds"}
          </Button>
        </section>

        <section className="mt-6 grid lg:grid-cols-[1.4fr_1fr] gap-6">
          <div className="rounded-lg border border-border bg-card p-4" data-testid="admin-stage2-attempts-list">
            <div className="flex items-center justify-between gap-3">
              <div className="font-semibold text-ink">Candidate attempts</div>
              <div className="text-xs text-muted-foreground">{rows.length} total · {rows.filter(r => r.status === "completed").length} completed</div>
            </div>
            <div className="mt-3 max-h-[680px] overflow-auto">
              {rows.length === 0 ? (
                <div className="text-sm text-muted-foreground">No Stage 2 attempts yet.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-2 pr-2">Candidate</th>
                      <th className="py-2 pr-2">Status</th>
                      <th className="py-2 pr-2">Outcome</th>
                      <th className="py-2 pr-2">Safety %</th>
                      <th className="py-2 pr-2">Profile</th>
                      <th className="py-2 pr-2">R/T/P/A</th>
                      <th className="py-2 pr-2" title="Critical response count · absolute red-line count · integrity flags">Risk</th>
                      <th className="py-2 pr-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const flags = Array.isArray(r.assessment_integrity_flags) ? r.assessment_integrity_flags : [];
                      const critical = r.critical_response_count ?? 0;
                      const redline = r.absolute_red_line_count ?? 0;
                      const risky = redline > 0 || critical > 2 || flags.length > 0;
                      return (
                        <tr
                          key={r.attempt_id}
                          className={`border-b border-border/70 cursor-pointer align-top ${selected === r.attempt_id ? "bg-amber/10" : "hover:bg-paper/40"}`}
                          onClick={() => openDetail(r.attempt_id)}
                          data-testid={`admin-stage2-row-${r.attempt_id}`}
                        >
                          <td className="py-2 pr-2">
                            <div className="font-semibold text-ink">{r.candidate_name || r.candidate_reference}</div>
                            <div className="text-[11px] text-muted-foreground">{r.candidate_reference}</div>
                            {r.date_completed && <div className="text-[11px] text-muted-foreground mt-0.5">done {String(r.date_completed).slice(0, 10)}</div>}
                          </td>
                          <td className="py-2 pr-2">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${r.status === "completed" ? "bg-emerald-100 text-emerald-900" : r.status === "in_progress" ? "bg-sky-100 text-sky-900" : "bg-stone-200 text-stone-700"}`}>{r.status}</span>
                          </td>
                          <td className="py-2 pr-2">{r.progression_outcome || "-"}</td>
                          <td className="py-2 pr-2 font-mono">{r.safety_percentage ?? "-"}</td>
                          <td className="py-2 pr-2">
                            {r.primary_profile ? (
                              <div>
                                <div className="font-semibold text-ink">{r.primary_profile}{r.secondary_profile ? ` · ${r.secondary_profile}` : ""}</div>
                                <div className="text-[10px] text-muted-foreground">{r.profile_strength || ""}</div>
                              </div>
                            ) : "-"}
                          </td>
                          <td className="py-2 pr-2 font-mono text-[11px]">
                            {r.R_score != null ? `${r.R_score}/${r.T_score}/${r.P_score}/${r.A_score}` : "-"}
                          </td>
                          <td className="py-2 pr-2">
                            {r.status !== "completed" ? "-" : (
                              <div className={`text-[11px] ${risky ? "text-rose-700 font-semibold" : "text-emerald-800"}`}>
                                {redline > 0 && <span title="Absolute red-line responses">🚫 {redline}</span>}
                                {critical > 0 && <span title="Critical unsafe responses" className={redline > 0 ? "ml-1" : ""}>⚠ {critical}</span>}
                                {flags.length > 0 && <div className="text-[10px] text-amber-800 mt-0.5">{flags.join(", ")}</div>}
                                {!redline && !critical && !flags.length && <span>✓ clean</span>}
                              </div>
                            )}
                          </td>
                          <td className="py-2 pr-2">
                            <Button
                              size="sm"
                              className="bg-amber text-ink hover:bg-amber-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                submitCandidateReset(r.candidate_id, r.candidate_name || r.candidate_reference);
                              }}
                            >
                            Reset latest
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4" data-testid="admin-stage2-attempt-detail">
            <div className="font-semibold text-ink">Attempt detail</div>
            {!detail ? (
              <div className="mt-3 text-sm text-muted-foreground">Select an attempt to inspect the behavioural profile, risk indicators and question-by-question audit trail.</div>
            ) : (
              <div className="mt-3 space-y-4">
                <div className="text-sm">
                  <div><span className="text-muted-foreground">Candidate:</span> <span className="text-ink font-semibold">{detail.candidate?.contact_name || "-"}</span></div>
                  <div><span className="text-muted-foreground">Attempt:</span> <span className="font-mono text-[11px]">{detail.attempt?.id}</span></div>
                  <div><span className="text-muted-foreground">Status:</span> {detail.attempt?.status}</div>
                  <div><span className="text-muted-foreground">Completed:</span> {String(detail.attempt?.completed_at || "-").slice(0, 19)}</div>
                  <div><span className="text-muted-foreground">Outcome:</span> <span className="font-semibold text-ink">{detail.attempt?.result?.decision?.progression_outcome || "-"}</span></div>
                  <div><span className="text-muted-foreground">Safety %:</span> <span className="font-mono">{detail.attempt?.result?.scores?.overall_safety_pct ?? "-"}</span></div>
                </div>

                {/* Behavioural profile viz */}
                {detail.attempt?.result?.profile && (
                  <div className="rounded-md border border-border p-3" data-testid="admin-stage2-behavioural-profile">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Behavioural profile</div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Primary:</span> <span className="font-semibold text-ink">{detail.attempt.result.profile.primary_tendency || "-"}</span>
                      {detail.attempt.result.profile.secondary_tendency && (
                        <> · <span className="text-muted-foreground">Secondary:</span> <span className="font-semibold text-ink">{detail.attempt.result.profile.secondary_tendency}</span></>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{detail.attempt.result.profile.combined_profile || ""} · strength {detail.attempt.result.profile.profile_strength_indicator || ""}</div>
                    <div className="mt-3 space-y-1.5">
                      {["R","T","P","A"].map(k => {
                        const raw = detail.attempt.result.profile[k];
                        const val = typeof raw === "number" ? raw : 0;
                        const total = ["R","T","P","A"].reduce((s,x) => s + (typeof detail.attempt.result.profile[x] === "number" ? detail.attempt.result.profile[x] : 0), 0) || 1;
                        const pct = Math.round((val / total) * 100);
                        const label = {R:"Reflective",T:"Traditional/Procedural",P:"Pragmatic",A:"Assertive"}[k];
                        return (
                          <div key={k} className="flex items-center gap-2 text-xs">
                            <div className="w-32 shrink-0"><span className="font-semibold text-ink">{k}</span> <span className="text-muted-foreground">{label}</span></div>
                            <div className="flex-1 bg-paper rounded h-2 overflow-hidden">
                              <div className={`h-full ${k === "R" ? "bg-sky-500" : k === "T" ? "bg-navy" : k === "P" ? "bg-amber-500" : "bg-rose-500"}`} style={{width: `${pct}%`}} />
                            </div>
                            <div className="w-14 text-right font-mono text-[11px]">{val} · {pct}%</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Risk / integrity indicators */}
                {detail.attempt?.result?.scores && (
                  <div className="rounded-md border border-border p-3" data-testid="admin-stage2-risk-panel">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Risk & integrity indicators</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <RiskStat label="Critical responses" value={detail.attempt.result.scores.critical_response_count} warn={(detail.attempt.result.scores.critical_response_count ?? 0) > 0} />
                      <RiskStat label="Absolute red-lines" value={detail.attempt.result.scores.absolute_red_line_count} warn={(detail.attempt.result.scores.absolute_red_line_count ?? 0) > 0} />
                      <RiskStat label="Unanswered" value={detail.attempt.result.scores.unanswered_count} />
                      <RiskStat label="Timed out" value={detail.attempt.result.scores.timeout_count} />
                      <RiskStat label="Rapid responses" value={detail.attempt.result.scores.rapid_response_count} />
                      <RiskStat label="Avg response (s)" value={detail.attempt.result.scores.average_response_time_seconds} />
                    </div>
                    {Array.isArray(detail.attempt.result.decision?.assessment_integrity_flags) && detail.attempt.result.decision.assessment_integrity_flags.length > 0 && (
                      <div className="mt-2 text-xs">
                        <span className="text-muted-foreground">Integrity flags:</span>{" "}
                        {detail.attempt.result.decision.assessment_integrity_flags.map((f) => (
                          <span key={f} className="inline-block bg-rose-100 text-rose-900 rounded px-1.5 py-0.5 text-[10px] font-semibold mr-1">{f}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Domain scores */}
                {detail.attempt?.result?.scores?.domain_scores_pct && (
                  <div className="rounded-md border border-border p-3" data-testid="admin-stage2-domain-scores">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Operational domain scores</div>
                    <div className="space-y-1">
                      {Object.entries(detail.attempt.result.scores.domain_scores_pct).map(([dom, pct]) => (
                        <div key={dom} className="flex items-center gap-2 text-xs">
                          <div className="w-44 shrink-0 text-ink">{dom.replace(/_/g," ")}</div>
                          <div className="flex-1 bg-paper rounded h-2 overflow-hidden">
                            <div className={`h-full ${pct >= 70 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-rose-500"}`} style={{width: `${Math.max(0, Math.min(100, pct))}%`}} />
                          </div>
                          <div className="w-12 text-right font-mono text-[11px]">{pct}%</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="rounded-md border border-border p-3 max-h-[220px] overflow-auto">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Question audit</div>
                  <div className="space-y-2 text-xs">
                    {(detail.question_audit || []).map((q) => (
                      <div key={q.question_position} className="border border-border rounded p-2">
                        <div className="font-semibold text-ink">Q{q.question_position} · {q.question_uid}</div>
                        <div className="text-muted-foreground mt-1">Selected: {q.selected_letter || "Unanswered"} → profile <span className="font-mono">{q.selected_profile || "-"}</span> · Score: {q.awarded_safety_score}{q.selected_was_critical ? " · CRITICAL" : ""}{q.timed_out ? " · timed out" : ""}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-md border border-border p-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Manual decision</div>
                  <Label className="mt-2">Decision</Label>
                  <select className="w-full mt-1 border border-border rounded-md px-2 py-2 text-sm" value={decision} onChange={(e) => setDecision(e.target.value)}>
                    <option value="approve_progression">Approve progression</option>
                    <option value="decline_progression">Decline progression</option>
                    <option value="assign_confirmation_assessment">Assign confirmation assessment</option>
                  </select>
                  <Label className="mt-3">Rationale</Label>
                  <Textarea className="mt-1" value={rationale} onChange={(e) => setRationale(e.target.value)} />
                  <Label className="mt-3">Notes (optional)</Label>
                  <Textarea className="mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} />
                  <Button className="mt-3 bg-navy text-bone hover:bg-lime-600 hover:text-ink" onClick={submitDecision}>Record decision</Button>
                </div>

                <div className="rounded-md border border-border p-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Authorise attempt reset</div>
                  <Label className="mt-2">Reset reason</Label>
                  <Input className="mt-1" value={resetReason} onChange={(e) => setResetReason(e.target.value)} />
                  <Label className="mt-3">Incident category</Label>
                  <Input className="mt-1" value={resetCategory} onChange={(e) => setResetCategory(e.target.value)} />
                  <Label className="mt-3">Administrator note</Label>
                  <Textarea className="mt-1" value={resetNote} onChange={(e) => setResetNote(e.target.value)} />
                  <Button className="mt-3 bg-amber text-ink hover:bg-amber-600" onClick={submitReset}>Authorise reset</Button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function RiskStat({ label, value, warn }) {
  return (
    <div className={`border rounded px-2 py-1.5 ${warn ? "border-rose-300 bg-rose-50" : "border-border bg-paper"}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-sm font-mono ${warn ? "text-rose-900 font-semibold" : "text-ink"}`}>{value ?? "-"}</div>
    </div>
  );
}
